import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { createCompanySchema, type CreateCompanyInput } from '@orion/contracts';
import { Button, Input } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { useResolveGoogleMapsUrl } from '../prospecting/api.js';
import { useCreateCompany } from './api.js';

/** Also doubles as the "manual add" path for a prospect that didn't turn
 * up in a Prospecting search (docs section "Prospecting") — pasting a
 * Google Maps share link looks the business up the same way the search
 * screen does and fills in what it finds; every field stays editable
 * either way. */
export function NewCompanyPage() {
  const navigate = useNavigate();
  const createCompany = useCreateCompany();
  const resolveUrl = useResolveGoogleMapsUrl();
  const [mapsUrl, setMapsUrl] = useState('');
  const [mapsLookupState, setMapsLookupState] = useState<'idle' | 'found' | 'not_found'>('idle');
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateCompanyInput>({
    resolver: zodResolver(createCompanySchema),
    defaultValues: { lifecycleStage: 'prospect', customFields: {} },
  });

  const lookupMapsUrl = async () => {
    if (!mapsUrl.trim()) return;
    setMapsLookupState('idle');
    const result = await resolveUrl.mutateAsync(mapsUrl.trim());
    if (!result) {
      setMapsLookupState('not_found');
      return;
    }
    setMapsLookupState('found');
    setValue('name', result.name, { shouldValidate: true });
    if (result.phone) setValue('phone', result.phone);
    if (result.website) setValue('website', result.website);
    if (result.address.formatted) setValue('address.formatted', result.address.formatted);
  };

  const onSubmit = async (input: CreateCompanyInput) => {
    const company = await createCompany.mutateAsync(input);
    await navigate({ to: '/companies/$companyId', params: { companyId: company.id } });
  };

  return (
    <>
      <PageHeader title="New company" />
      <main className="flex flex-1 justify-center p-7">
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex w-full max-w-lg flex-col gap-4">
          <div className="flex flex-col gap-1.5 rounded-card border border-border bg-surface-subtle p-3">
            <label className="text-[13px] font-semibold text-ink" htmlFor="maps-url">
              Google Maps URL <span className="font-normal text-text-muted">(optional — looks the business up for you)</span>
            </label>
            <div className="flex gap-2">
              <input
                id="maps-url"
                type="url"
                value={mapsUrl}
                onChange={(e) => setMapsUrl(e.target.value)}
                placeholder="https://www.google.com/maps/place/..."
                className="h-9 flex-1 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
              />
              <Button type="button" size="sm" variant="secondary" onClick={lookupMapsUrl} loading={resolveUrl.isPending}>
                Look up
              </Button>
            </div>
            {mapsLookupState === 'found' && <p className="text-xs font-medium text-success">Found it — review the fields below.</p>}
            {mapsLookupState === 'not_found' && (
              <p className="text-xs font-medium text-text-muted">Couldn't match that link — fill in the fields by hand instead.</p>
            )}
          </div>

          <Input label="Name" error={errors.name?.message} {...register('name')} />
          <Input label="Domain" error={errors.domain?.message} {...register('domain')} />
          <Input label="Website" error={errors.website?.message} {...register('website')} />
          <Input label="Industry" error={errors.industry?.message} {...register('industry')} />
          <Input label="Phone" error={errors.phone?.message} {...register('phone')} />
          <Input label="Email" error={errors.email?.message} {...register('email')} />
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Description
            <textarea {...register('description')} rows={3} className="rounded-control border border-border-strong bg-surface px-3 py-2 text-sm font-normal text-ink" />
            {errors.description?.message && <span className="text-xs font-normal text-danger">{errors.description.message}</span>}
          </label>

          <div className="mt-1 border-t border-divider pt-4">
            <h3 className="mb-3 text-[13px] font-semibold text-ink">Address</h3>
            <div className="flex flex-col gap-3">
              <Input label="Street address" error={errors.address?.line1?.message} {...register('address.line1')} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="City" error={errors.address?.city?.message} {...register('address.city')} />
                <Input label="State / province" error={errors.address?.state?.message} {...register('address.state')} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Postal code" error={errors.address?.postalCode?.message} {...register('address.postalCode')} />
                <Input label="Country" error={errors.address?.country?.message} {...register('address.country')} />
              </div>
            </div>
          </div>

          <div className="mt-2 flex justify-end">
            <Button type="submit" loading={isSubmitting}>
              Save company
            </Button>
          </div>
        </form>
      </main>
    </>
  );
}
